import { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Input, Select } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { z } from "zod";
import { RiArrowGoBackFill } from "react-icons/ri";
import { AiOutlineSave } from "react-icons/ai";
import {
  StyledModal,
  FormContainer,
  FormItem,
  RequiredLabel,
  ButtonContainer,
  CancelButton,
  ConfirmButton,
  CharacterCount,
  SelectReason,
} from "./styles";
import { getRejectionReasonsService } from "../../../../service/uploadFlow.service";
import { useSelector } from "react-redux";

const OBSERVATION_MAX_LENGTH = 100;

const RejectDocumentModal = ({
  isClientUX = false,
  open,
  onClose,
  onConfirm,
  onGoBack,
  loading = false,
  isPackage = false,
}) => {
  const [form] = Form.useForm();
  const intl = useIntl();

  const { token, user } = useSelector((state) => state.auth);
  const isAdmin = user?.role?.type === "ADMINISTRATOR";
  const [formData, setFormData] = useState({
    rejectionReasonId: undefined,
    observation: "",
  });

  const [errors, setErrors] = useState({});
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [reasons, setReasons] = useState([]);

  const rejectDocumentSchema = useMemo(
    () =>
      z.object({
        rejectionReasonId: z
          .number({
            required_error: "required_field",
            invalid_type_error: "required_field",
          })
          .int("required_field")
          .positive("required_field"),
        observation: z
          .string()
          .max(OBSERVATION_MAX_LENGTH, "reject_document.observation.max")
          .optional(),
      }),
    []
  );

  const getReasonsFromResponse = useCallback((data) => {
    if (Array.isArray(data?.rejectionReasonsPage?.content)) {
      return data.rejectionReasonsPage.content;
    }

    if (Array.isArray(data?.content)) {
      return data.content;
    }

    return [];
  }, []);

  const fetchReasons = useCallback(async () => {
    try {
      setReasonsLoading(true);

      const { data } = await getRejectionReasonsService(
        {
          page: 0,
          size: 200,
          sort: "createdAt",
          direction: "DESC",
        },
        token
      );

      const items = getReasonsFromResponse(data);

      const mappedReasons = items
        .filter((item) => item?.active !== false)
        .map((item) => ({
          value: item.id,
          label: item.description ?? "",
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        );

      setReasons(mappedReasons);
    } catch (error) {
      // mantém a tela funcionando mesmo se falhar
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setReasonsLoading(false);
    }
  }, [getReasonsFromResponse, token]);

  useEffect(() => {
    if (!open) return;
    fetchReasons();
  }, [fetchReasons, open]);

  const clearFieldError = useCallback(
    (field) => {
      if (!errors[field]) return;

      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    },
    [errors]
  );

  const handleInputChange = useCallback(
    (field, value) => {
      const truncatedValue =
        typeof value === "string"
          ? value.slice(0, OBSERVATION_MAX_LENGTH)
          : value;

      setFormData((prev) => ({
        ...prev,
        [field]: truncatedValue,
      }));

      clearFieldError(field);

      form.setFieldsValue({
        [field]: truncatedValue,
      });
    },
    [clearFieldError, form]
  );

  const handleReasonChange = useCallback(
    (value) => {
      // value já vem como number (quando Option value é number)
      setFormData((prev) => ({
        ...prev,
        rejectionReasonId: value,
      }));

      clearFieldError("rejectionReasonId");

      form.setFieldsValue({
        rejectionReasonId: value,
      });
    },
    [clearFieldError, form]
  );

  const handleClose = useCallback(() => {
    setFormData({
      rejectionReasonId: undefined,
      observation: "",
    });
    setErrors({});
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const handleCloseGoBack = useCallback(() => {
    setFormData({
      rejectionReasonId: undefined,
      observation: "",
    });
    setErrors({});
    form.resetFields();
    onGoBack();
  }, [form, onGoBack]);

  const handleSubmit = useCallback(() => {
    try {
      const validatedData = rejectDocumentSchema.parse({
        rejectionReasonId: formData.rejectionReasonId,
        observation: formData.observation?.trim() || undefined,
      });

      setErrors({});
      onConfirm(validatedData);
      handleClose();
    } catch (error) {
      if (!(error instanceof z.ZodError)) return;

      const formErrors = {};

      error.errors.forEach((err) => {
        const field = err.path[0];

        formErrors[field] = intl.formatMessage({
          id: err.message,
          defaultMessage: intl.formatMessage({ id: "required_field" }),
        });
      });

      setErrors(formErrors);
    }
  }, [formData, handleClose, intl, onConfirm, rejectDocumentSchema]);

  return (
    <StyledModal
      alterUx={isClientUX}
      title={
        isPackage ? (
          <FormattedMessage id="reject_package" />
        ) : (
          <FormattedMessage id="reject_document" />
        )
      }
      open={open}
      onClose={handleClose}
      footer={null}
      width={667}
      centered
      closeIcon={<></>}
    >
      <FormContainer>
        <Form form={form} layout="vertical">
          <FormItem
            label={
              <span>
                <FormattedMessage id="rejection_reason" />
                <RequiredLabel>*</RequiredLabel>
              </span>
            }
            validateStatus={errors.rejectionReasonId ? "error" : ""}
            help={errors.rejectionReasonId}
          >
            <SelectReason
              style={{ width: "100%" }}
              dropdownMatchSelectWidth
              value={formData.rejectionReasonId}
              onChange={handleReasonChange}
              placeholder={intl.formatMessage({ id: "rejection_reason" })}
              loading={reasonsLoading}
              disabled={loading}
              showSearch
              filterOption={(input, option) =>
                String(option?.children ?? "")
                  .toLowerCase()
                  .includes(String(input ?? "").toLowerCase())
              }
            >
              {reasons.map((reason) => (
                <Select.Option key={reason.value} value={reason.value}>
                  {reason.label}
                </Select.Option>
              ))}
            </SelectReason>
          </FormItem>

          <FormItem
            label={<FormattedMessage id="additional_comments" />}
            validateStatus={errors.observation ? "error" : ""}
            help={errors.observation}
          >
            <Input.TextArea
              value={formData.observation}
              onChange={(e) => handleInputChange("observation", e.target.value)}
              placeholder={intl.formatMessage({ id: "additional_comments" })}
              rows={6}
              maxLength={OBSERVATION_MAX_LENGTH}
            />
            <CharacterCount>
              {formData.observation.length}/{OBSERVATION_MAX_LENGTH}
            </CharacterCount>
          </FormItem>
        </Form>

        <ButtonContainer>
          <CancelButton
            alterUx={isClientUX}
            onClick={handleCloseGoBack}
            disabled={loading}
          >
            {!isClientUX && <RiArrowGoBackFill size={16} />}
            <FormattedMessage id="back" />
          </CancelButton>

          <ConfirmButton
            alterUx={isClientUX}
            onClick={handleSubmit}
            loading={loading}
          >
            {!isClientUX && <AiOutlineSave size={16} />}
            <FormattedMessage id="save" />
          </ConfirmButton>
        </ButtonContainer>
      </FormContainer>
    </StyledModal>
  );
};

export default RejectDocumentModal;
